import React, { createContext, useContext, useState, ReactNode, useEffect, useCallback } from 'react';
import { ResourceType } from './PermissionsContext';

export interface NavigationItem {
  id: string;
  label: string;
  icon: string;
  path?: string;
  children?: NavigationItem[];
  requiredResource?: ResourceType; // Resource that must have 'view' permission
  permissions?: string[];
  roles?: string[]; // Roles that can see this item (deprecated, use requiredResource)
}

interface NavigationContextType {
  navigationItems: NavigationItem[];
  setNavigationItems: (items: NavigationItem[]) => void;
  loadNavigationFromDatabase: () => Promise<void>;
  addNavigationItem: (item: NavigationItem) => void;
  navigate: (path: string) => void;
  currentPath: string;
  filterNavigationByPermissions: (items: NavigationItem[], hasPermission: (resource: ResourceType, action: 'view') => boolean | 'all' | 'own') => NavigationItem[];
}

const NavigationContext = createContext<NavigationContextType | undefined>(undefined);

// Default navigation items (will be replaced by database data)
const defaultNavigationItems: NavigationItem[] = [
  { 
    id: 'dashboard', 
    label: 'Dashboard', 
    icon: 'LayoutDashboard', 
    path: '/dashboard',
    requiredResource: 'Dashboard'
  },
];

export const NavigationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [navigationItems, setNavigationItems] = useState<NavigationItem[]>(defaultNavigationItems);
  const [currentPath, setCurrentPath] = useState<string>(window.location.pathname + window.location.search);

  // Client-side navigation without page reload
  const navigate = (path: string) => {
    // Handle relative paths (add leading slash if missing)
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    
    // Extract pathname and query string
    const [pathname, queryString] = normalizedPath.split('?');
    const fullPath = queryString ? `${pathname}?${queryString}` : pathname;
    
    // Only navigate if pathname changed (ignore query param changes for state comparison)
    const currentPathname = currentPath.split('?')[0];
    if (pathname === currentPathname && fullPath === currentPath) {
      // Already on this exact path
      return;
    }
    
    // For admin pages with query parameters (legacy), use full page reload to ensure proper routing
    // Path-based admin routes (e.g., /admin/subscriptions) don't need reload
    if (pathname === '/admin' && queryString) {
      window.location.href = fullPath;
      return;
    }
    
    // Update URL and state
    window.history.pushState({}, '', fullPath);
    setCurrentPath(fullPath);
  };

  // Handle browser back/forward buttons
  useEffect(() => {
    const handlePopState = () => {
      // Include query string in currentPath
      const fullPath = window.location.pathname + window.location.search;
      setCurrentPath(fullPath);
    };
    
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const filterNavigationByRole = (items: NavigationItem[], userRole: string): NavigationItem[] => {
    return items.filter(item => {
      // If no roles specified, item is visible to all
      if (!item.roles || item.roles.length === 0) {
        return true;
      }
      // Check if user's role is in the allowed roles
      return item.roles.includes(userRole);
    }).map(item => ({
      ...item,
      // Recursively filter children if they exist
      children: item.children ? filterNavigationByRole(item.children, userRole) : undefined
    }));
  };

  const filterNavigationByPermissions = useCallback((
    items: NavigationItem[], 
    hasPermission: (resource: ResourceType, action: 'view') => boolean | 'all' | 'own'
  ): NavigationItem[] => {
    // First pass: filter items and recursively filter children
    const filtered = items.map(item => ({
      ...item,
      // Recursively filter children if they exist
      children: item.children ? filterNavigationByPermissions(item.children, hasPermission) : undefined
    })).filter(item => {
      // If no required resource, item is visible to all (but hide if all children are filtered)
      if (!item.requiredResource) {
        // If item has children, only show if at least one child is visible
        if (item.children && item.children.length > 0) {
          return item.children.length > 0;
        }
        return true;
      }
      // Check if user has view permission for the required resource (truthy check includes 'all', 'own', true)
      return !!hasPermission(item.requiredResource, 'view');
    });
    
    return filtered;
  }, []);

  const loadNavigationFromDatabase = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        // No token, use default navigation
        setNavigationItems(defaultNavigationItems);
        return;
      }

      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

      // Get user info to filter navigation
      const userResponse = await fetch(`${apiUrl}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      let userRole = 'Account User'; // Default role
      if (userResponse.ok) {
        const userData = await userResponse.json();
        if (userData.success && userData.user) {
          userRole = userData.user.role;
        }
      }

      const response = await fetch(`${apiUrl}/api/navigation`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        // Fallback to default items on error, filtered by role
        setNavigationItems(filterNavigationByRole(defaultNavigationItems, userRole));
        return;
      }

      const data = await response.json();
      if (data.success) {
        // Only use database items if they exist, otherwise use defaults
        if (data.data && data.data.length > 0) {
          setNavigationItems(filterNavigationByRole(data.data, userRole));
        } else {
          setNavigationItems(filterNavigationByRole(defaultNavigationItems, userRole));
        }
      } else {
        // Fallback to default items, filtered by role
        setNavigationItems(filterNavigationByRole(defaultNavigationItems, userRole));
      }
    } catch (error) {
      console.error('Failed to load navigation from database:', error);
      // Fallback to default items
      setNavigationItems(defaultNavigationItems);
    }
  };

  const addNavigationItem = (item: NavigationItem) => {
    setNavigationItems(prev => {
      // Check if item already exists
      if (prev.some(existingItem => existingItem.id === item.id)) {
        return prev;
      }
      return [...prev, item];
    });
  };

  // Load navigation on mount
  useEffect(() => {
    loadNavigationFromDatabase();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Expose refetch function globally for impersonation
  useEffect(() => {
    (window as any).__reloadNavigation = loadNavigationFromDatabase;
    return () => {
      delete (window as any).__reloadNavigation;
    };
  }, []);

  return (
    <NavigationContext.Provider value={{
      navigationItems,
      setNavigationItems,
      loadNavigationFromDatabase,
      addNavigationItem,
      navigate,
      currentPath,
      filterNavigationByPermissions
    }}>
      {children}
    </NavigationContext.Provider>
  );
};

export const useNavigation = (): NavigationContextType => {
  const context = useContext(NavigationContext);
  if (context === undefined) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  return context;
};
